import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HTTP_PAYLOAD_TOO_LARGE = 413;

const MONTHS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
  { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
  { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' },
];

// ─── Hook: Data Fetching ───

// ─── Data Transformation Utils ───

function computeGstr1Totals(summary) {
  const t = { taxable: 0, igst: 0, cgst: 0, sgst: 0, total: 0 };
  Object.values(summary).forEach(cat => {
    t.taxable += cat.taxable_value || 0;
    t.igst += cat.igst || 0;
    t.cgst += cat.cgst || 0;
    t.sgst += cat.sgst || 0;
    t.total += cat.invoice_value || 0;
  });
  return t;
}

function computeReconStats(reconciliation) {
  return {
    total: reconciliation.length,
    matched: reconciliation.filter(r => r.status === 'Matched').length,
    variance: reconciliation.filter(r => r.status === 'Value Mismatch').length,
    onlyBooks: reconciliation.filter(r => r.status === 'Only in Books').length,
    onlyEinv: reconciliation.filter(r => r.status === 'Only in E-Invoice').length,
  };
}

function filterB2BTransactions(transactions) {
  return transactions.filter(t => t.category?.includes('B2B'));
}

function filterCDNTransactions(transactions) {
  return transactions.filter(t => ['Debit Note', 'Credit Note'].includes(t.voucher_type));
}

// ─── Hook: Data Fetching & State ───

function useFilingData(clientId, yearInt, monthInt) {
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  const [transactions, setTransactions] = useState([]);
  const [gstr1Summary, setGstr1Summary] = useState({});
  const [b2bData, setB2bData] = useState(null);
  const [b2cData, setB2cData] = useState(null);
  const [hsnSummary, setHsnSummary] = useState([]);
  const [reconciliation, setReconciliation] = useState([]);
  const [gstr3bSummary, setGstr3bSummary] = useState(null);
  const [salesRegister, setSalesRegister] = useState(null);
  const [inputTransactions, setInputTransactions] = useState([]);
  const [inputSummary, setInputSummary] = useState(null);
  const [gstr2bEntries, setGstr2bEntries] = useState([]);
  const [itcRecon, setItcRecon] = useState([]);
  const [rcmWorking, setRcmWorking] = useState(null);
  const [gstr3bFull, setGstr3bFull] = useState(null);
  const [diffTracker, setDiffTracker] = useState(null);
  const [anomalies, setAnomalies] = useState(null);
  const [itcRegister, setItcRegister] = useState(null);

  const fetchClient = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/clients/${clientId}`, { withCredentials: true });
      setClient(response.data);
      setFetchError(null);
    } catch (err) {
      setFetchError(err.response?.data?.detail || 'Failed to load client');
      navigate('/dashboard');
    }
  }, [clientId, navigate]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const [txnRes, summaryRes, b2cRes, hsnRes, reconRes, gstr3bRes, salesRegRes, inputTxnRes, inputSumRes, gstr2bRes, itcReconRes, rcmRes, gstr3bFullRes, diffTrackerRes, anomalyRes, itcRegRes, b2bRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/transactions/${clientId}/${yearInt}/${monthInt}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/reports/gstr1/summary/${clientId}/${yearInt}/${monthInt}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/reports/gstr1/b2c/${clientId}/${yearInt}/${monthInt}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/reports/gstr1/hsn/${clientId}/${yearInt}/${monthInt}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/reports/gstr1/reconciliation/${clientId}/${yearInt}/${monthInt}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/reports/gstr3b/summary/${clientId}/${yearInt}/${monthInt}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/reports/gstr1/sales-register/${clientId}/${yearInt}/${monthInt}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/input-transactions/${clientId}/${yearInt}/${monthInt}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/reports/gstr3b/input-summary/${clientId}/${yearInt}/${monthInt}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/gstr2b-entries/${clientId}/${yearInt}/${monthInt}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/itc-reconciliation/${clientId}/${yearInt}/${monthInt}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/rcm-working/${clientId}/${yearInt}/${monthInt}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/gstr3b-full/${clientId}/${yearInt}/${monthInt}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/difference-tracker/${clientId}/${yearInt}/${monthInt}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/anomalies/${clientId}/${yearInt}/${monthInt}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/reports/gstr3b/itc-register/${clientId}/${yearInt}/${monthInt}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/reports/gstr1/b2b/${clientId}/${yearInt}/${monthInt}`, { withCredentials: true }),
      ]);
      setTransactions(txnRes.data);
      setGstr1Summary(summaryRes.data);
      setB2bData(b2bRes.data);
      setB2cData(b2cRes.data);
      setHsnSummary(hsnRes.data);
      setReconciliation(reconRes.data);
      setGstr3bSummary(gstr3bRes.data);
      setSalesRegister(salesRegRes.data);
      setInputTransactions(inputTxnRes.data);
      setInputSummary(inputSumRes.data);
      setGstr2bEntries(gstr2bRes.data);
      setItcRecon(itcReconRes.data);
      setRcmWorking(rcmRes.data);
      setGstr3bFull(gstr3bFullRes.data);
      setDiffTracker(diffTrackerRes.data);
      setAnomalies(anomalyRes.data);
      setItcRegister(itcRegRes.data);
    } catch (err) {
      setFetchError(err.response?.data?.detail || 'Failed to load filing data');
    } finally {
      setLoading(false);
    }
  }, [clientId, yearInt, monthInt]);

  useEffect(() => { fetchClient(); }, [fetchClient]);
  useEffect(() => { if (client) fetchData(); }, [client, fetchData]);

  const totals = useMemo(() => computeGstr1Totals(gstr1Summary), [gstr1Summary]);

  const b2bTransactions = useMemo(() => filterB2BTransactions(transactions), [transactions]);

  const cdnTransactions = useMemo(() => filterCDNTransactions(transactions), [transactions]);

  const reconStats = useMemo(() => computeReconStats(reconciliation), [reconciliation]);

  return {
    client, loading, fetchError, fetchData,
    transactions, setTransactions, gstr1Summary, b2bData, b2cData, hsnSummary, reconciliation, gstr3bSummary, salesRegister,
    inputTransactions, inputSummary,
    gstr2bEntries, itcRecon, rcmWorking, gstr3bFull, diffTracker, anomalies, itcRegister,
    totals, b2bTransactions, cdnTransactions, reconStats,
  };
}

// ─── Hook: Upload Management ───

// Chunked upload constants for COA files exceeding proxy body size limits
const COA_CHUNK_SIZE = 512 * 1024; // 512KB per chunk
const COA_CHUNKED_THRESHOLD = 500 * 1024; // Use chunked upload for files > 500KB

function classifyUploadError(err) {
  if (err.response?.data?.detail) return err.response.data.detail;
  if (err.response?.status === HTTP_PAYLOAD_TOO_LARGE) return 'File too large. Try reducing the file size or contact support.';
  if (err.response?.status === 501) return 'File too large for direct upload. Retrying with chunked upload...';
  if (err.response?.status) return `Upload failed (HTTP ${err.response.status}). Please try again.`;
  if (err.code === 'ERR_NETWORK') return 'Network error — file may be too large or server is unreachable.';
  if (err.message) return err.message;
  return 'Upload failed';
}

function shouldRetryChunked(endpoint, err) {
  return endpoint === 'coa' && (err.response?.status === 501 || err.code === 'ERR_NETWORK');
}

function buildFormData(files, clientId, monthInt, yearInt, endpoint) {
  const formData = new FormData();
  for (let i = 0; i < files.length; i++) {
    formData.append('files', files[i]);
  }
  formData.append('client_id', clientId);
  if (endpoint !== 'rate-master' && endpoint !== 'ledger-rate-master') {
    formData.append('month', monthInt);
    formData.append('year', yearInt);
  }
  return formData;
}

function useFilingUpload(clientId, yearInt, monthInt, fetchData) {
  const [uploading, setUploading] = useState({});
  const [uploadStatus, setUploadStatus] = useState({});
  const [uploadError, setUploadError] = useState(null);

  useEffect(() => { setUploadStatus({}); setUploadError(null); }, [yearInt, monthInt]);

  const uploadCoaChunked = useCallback(async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const totalBytes = arrayBuffer.byteLength;
    const totalChunks = Math.ceil(totalBytes / COA_CHUNK_SIZE);

    const initRes = await axios.post(`${BACKEND_URL}/api/upload/coa/init`, {
      client_id: clientId, month: monthInt, year: yearInt,
      filename: file.name, total_chunks: totalChunks,
    }, { withCredentials: true });
    const uploadId = initRes.data.upload_id;

    for (let i = 0; i < totalChunks; i++) {
      const start = i * COA_CHUNK_SIZE;
      const end = Math.min(start + COA_CHUNK_SIZE, totalBytes);
      const chunk = arrayBuffer.slice(start, end);
      const base64 = btoa(String.fromCharCode(...new Uint8Array(chunk)));
      await axios.post(`${BACKEND_URL}/api/upload/coa/chunk`, {
        upload_id: uploadId, chunk_index: i, data: base64,
      }, { withCredentials: true });
    }

    return axios.post(`${BACKEND_URL}/api/upload/coa/complete`, { upload_id: uploadId }, { withCredentials: true });
  }, [clientId, yearInt, monthInt]);

  const handleUpload = useCallback(async (type, endpoint, fileRef) => {
    const files = fileRef.current?.files;
    if (!files || files.length === 0) return;

    setUploading(prev => ({ ...prev, [type]: true }));
    setUploadStatus(prev => ({ ...prev, [type]: null }));
    setUploadError(null);

    try {
      // Use chunked upload for large COA files
      if (endpoint === 'coa' && files[0].size > COA_CHUNKED_THRESHOLD) {
        const response = await uploadCoaChunked(files[0]);
        setUploadStatus(prev => ({ ...prev, [type]: { success: true, message: response.data.message } }));
        if (fileRef.current) fileRef.current.value = '';
        fetchData();
        return;
      }

      const formData = buildFormData(files, clientId, monthInt, yearInt, endpoint);
      const response = await axios.post(`${BACKEND_URL}/api/upload/${endpoint}`, formData, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000, maxContentLength: Infinity, maxBodyLength: Infinity,
      });

      setUploadStatus(prev => ({ ...prev, [type]: { success: true, message: response.data.message } }));
      if (fileRef.current) fileRef.current.value = '';
      fetchData();
    } catch (err) {
      const message = classifyUploadError(err);

      // Auto-retry COA with chunked upload on 501 or network error
      if (shouldRetryChunked(endpoint, err)) {
        try {
          setUploadStatus(prev => ({ ...prev, [type]: { success: false, message: 'Large file detected — switching to chunked upload...' } }));
          const response = await uploadCoaChunked(files[0]);
          setUploadStatus(prev => ({ ...prev, [type]: { success: true, message: response.data.message } }));
          if (fileRef.current) fileRef.current.value = '';
          fetchData();
          return;
        } catch (retryErr) {
          const retryMessage = retryErr.response?.data?.detail || 'Chunked upload also failed. Please try again.';
          setUploadStatus(prev => ({ ...prev, [type]: { success: false, message: retryMessage } }));
          setUploadError(retryMessage);
          return;
        }
      }

      setUploadStatus(prev => ({ ...prev, [type]: { success: false, message } }));
      setUploadError(message);
    } finally {
      setUploading(prev => ({ ...prev, [type]: false }));
    }
  }, [clientId, yearInt, monthInt, fetchData, uploadCoaChunked]);

  const handleExport = useCallback(async (reportType, format = 'xlsx') => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/reports/export/${clientId}/${yearInt}/${monthInt}/${reportType}?format=${format}`, { withCredentials: true, responseType: 'blob' });
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportType}_${yearInt}_${String(monthInt).padStart(2, '0')}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setUploadError(err.response?.data?.detail || 'Export failed');
    }
  }, [clientId, yearInt, monthInt]);

  return { uploading, uploadStatus, setUploadStatus, uploadError, handleUpload, handleExport };
}

// ─── Hook: Transaction Actions ───

function useExcludeActions(transactions, fetchData) {
  const [excludeTxn, setExcludeTxn] = useState(null);
  const [excludeReason, setExcludeReason] = useState('');
  const [actionError, setActionError] = useState(null);

  const handleToggleExclude = useCallback(async (transactionId, excluded) => {
    setActionError(null);
    if (excluded) {
      const txn = transactions.find(t => t.id === transactionId);
      setExcludeTxn(txn);
      setExcludeReason('');
      return;
    }
    try {
      await axios.put(`${BACKEND_URL}/api/transactions/${transactionId}/exclude`, { excluded: false, exclude_reason: null }, { withCredentials: true });
      fetchData();
    } catch (err) {
      setActionError(err.response?.data?.detail || 'Failed to update exclusion');
    }
  }, [transactions, fetchData]);

  const handleConfirmExclude = useCallback(async () => {
    if (!excludeTxn || !excludeReason.trim()) return;
    setActionError(null);
    try {
      await axios.put(`${BACKEND_URL}/api/transactions/${excludeTxn.id}/exclude`, { excluded: true, exclude_reason: excludeReason.trim() }, { withCredentials: true });
      setExcludeTxn(null);
      setExcludeReason('');
      fetchData();
    } catch (err) {
      setActionError(err.response?.data?.detail || 'Failed to exclude transaction');
    }
  }, [excludeTxn, excludeReason, fetchData]);

  const handleBulkExclude = useCallback(async (transactionIds, reason) => {
    if (!transactionIds.length || !reason.trim()) return;
    setActionError(null);
    try {
      await axios.put(`${BACKEND_URL}/api/transactions/bulk-exclude`, { transaction_ids: transactionIds, exclude_reason: reason.trim() }, { withCredentials: true });
      fetchData();
      return true;
    } catch (err) {
      setActionError(err.response?.data?.detail || 'Failed to bulk exclude');
      return false;
    }
  }, [fetchData]);

  return { excludeTxn, setExcludeTxn, excludeReason, setExcludeReason, actionError, handleToggleExclude, handleConfirmExclude, handleBulkExclude };
}

function useCategoryActions(setTransactions, fetchData) {
  const [editingTxn, setEditingTxn] = useState(null);
  const [newCategory, setNewCategory] = useState('');
  const [actionError, setActionError] = useState(null);

  const handleCategoryUpdate = useCallback(async () => {
    if (!editingTxn || !newCategory) return;
    setActionError(null);
    try {
      await axios.put(`${BACKEND_URL}/api/transactions/${editingTxn.id}/category`, { category: newCategory }, { withCredentials: true });
      setTransactions(prev => prev.map(t => t.id === editingTxn.id ? { ...t, category: newCategory } : t));
      setEditingTxn(null);
      fetchData();
    } catch (err) {
      setActionError(err.response?.data?.detail || 'Failed to update category');
    }
  }, [editingTxn, newCategory, setTransactions, fetchData]);

  const onEditTxn = useCallback((txn) => { setEditingTxn(txn); setNewCategory(txn.category); }, []);

  return { editingTxn, setEditingTxn, newCategory, setNewCategory, actionError, handleCategoryUpdate, onEditTxn };
}

function useFilingActions(transactions, setTransactions, fetchData) {
  const exclude = useExcludeActions(transactions, fetchData);
  const category = useCategoryActions(setTransactions, fetchData);

  return {
    editingTxn: category.editingTxn, setEditingTxn: category.setEditingTxn,
    newCategory: category.newCategory, setNewCategory: category.setNewCategory,
    excludeTxn: exclude.excludeTxn, setExcludeTxn: exclude.setExcludeTxn,
    excludeReason: exclude.excludeReason, setExcludeReason: exclude.setExcludeReason,
    actionError: exclude.actionError || category.actionError,
    handleToggleExclude: exclude.handleToggleExclude,
    handleConfirmExclude: exclude.handleConfirmExclude,
    handleBulkExclude: exclude.handleBulkExclude,
    handleCategoryUpdate: category.handleCategoryUpdate,
    onEditTxn: category.onEditTxn,
  };
}

// ─── Main Facade Hook ───

export default function useClientFiling() {
  const { clientId, year, month } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('gstr1');
  const [gstr1Tab, setGstr1Tab] = useState('summary');

  const years = useMemo(() => Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i), []);
  const monthInt = parseInt(month);
  const yearInt = parseInt(year);

  const data = useFilingData(clientId, yearInt, monthInt);
  const upload = useFilingUpload(clientId, yearInt, monthInt, data.fetchData);
  const actions = useFilingActions(data.transactions, data.setTransactions, data.fetchData);

  const setUploadStatus = upload.setUploadStatus;
  const fetchData = data.fetchData;
  const clientName = data.client?.name;

  const handlePeriodChange = useCallback((newYear, newMonth) => {
    setUploadStatus({});
    navigate(`/clients/${clientId}/filing/${newYear}/${newMonth}`);
  }, [clientId, navigate, setUploadStatus]);

  const deleteClientData = useCallback(async () => {
    try {
      const response = await axios.delete(`${BACKEND_URL}/api/data/${clientId}/${yearInt}/${monthInt}`, { withCredentials: true });
      setUploadStatus({ sales: { success: true, message: response.data.message } });
      fetchData();
    } catch (err) {
      setUploadStatus({ sales: { success: false, message: err.response?.data?.detail || 'Delete failed' } });
    }
  }, [clientId, yearInt, monthInt, setUploadStatus, fetchData]);

  const deleteGstr1Data = useCallback(async () => {
    try {
      const response = await axios.delete(`${BACKEND_URL}/api/data/${clientId}/${yearInt}/${monthInt}/gstr1`, { withCredentials: true });
      setUploadStatus({ sales: { success: true, message: response.data.message } });
      fetchData();
    } catch (err) {
      setUploadStatus({ sales: { success: false, message: err.response?.data?.detail || 'Delete failed' } });
    }
  }, [clientId, yearInt, monthInt, setUploadStatus, fetchData]);

  const deleteGstr3bData = useCallback(async () => {
    try {
      const response = await axios.delete(`${BACKEND_URL}/api/data/${clientId}/${yearInt}/${monthInt}/gstr3b`, { withCredentials: true });
      setUploadStatus({ sales: { success: true, message: response.data.message } });
      fetchData();
    } catch (err) {
      setUploadStatus({ sales: { success: false, message: err.response?.data?.detail || 'Delete failed' } });
    }
  }, [clientId, yearInt, monthInt, setUploadStatus, fetchData]);

  const handleDeleteAllData = useCallback(() => {
    const label = MONTHS.find(m => m.value === monthInt)?.label;
    if (!window.confirm(`Delete ALL uploaded data for ${clientName} - ${label} ${yearInt}?\n\nThis will remove all transactions, e-invoices, input register, 2B entries, and reconciliation data.`)) return;
    deleteClientData();
  }, [clientName, monthInt, yearInt, deleteClientData]);

  const handleDeleteGstr1 = useCallback(() => {
    const label = MONTHS.find(m => m.value === monthInt)?.label;
    if (!window.confirm(`Delete GSTR-1 data for ${clientName} - ${label} ${yearInt}?\n\nThis will remove sales transactions, e-invoices, and COA data.`)) return;
    deleteGstr1Data();
  }, [clientName, monthInt, yearInt, deleteGstr1Data]);

  const handleDeleteGstr3b = useCallback(() => {
    const label = MONTHS.find(m => m.value === monthInt)?.label;
    if (!window.confirm(`Delete GSTR-3B data for ${clientName} - ${label} ${yearInt}?\n\nThis will remove input register, GSTR-2B entries, ITC reconciliation, and difference tracker data.`)) return;
    deleteGstr3bData();
  }, [clientName, monthInt, yearInt, deleteGstr3bData]);

  return {
    clientId, monthInt, yearInt, years,
    client: data.client, activeTab, setActiveTab, gstr1Tab, setGstr1Tab,
    loading: data.loading, uploading: upload.uploading, uploadStatus: upload.uploadStatus,
    transactions: data.transactions, gstr1Summary: data.gstr1Summary, b2bData: data.b2bData, b2cData: data.b2cData,
    hsnSummary: data.hsnSummary, reconciliation: data.reconciliation, gstr3bSummary: data.gstr3bSummary,
    salesRegister: data.salesRegister,
    inputTransactions: data.inputTransactions, inputSummary: data.inputSummary,
    gstr2bEntries: data.gstr2bEntries, itcRecon: data.itcRecon, rcmWorking: data.rcmWorking, gstr3bFull: data.gstr3bFull, diffTracker: data.diffTracker, anomalies: data.anomalies, itcRegister: data.itcRegister,
    editingTxn: actions.editingTxn, setEditingTxn: actions.setEditingTxn,
    newCategory: actions.newCategory, setNewCategory: actions.setNewCategory,
    excludeTxn: actions.excludeTxn, setExcludeTxn: actions.setExcludeTxn,
    excludeReason: actions.excludeReason, setExcludeReason: actions.setExcludeReason,
    totals: data.totals, b2bTransactions: data.b2bTransactions, cdnTransactions: data.cdnTransactions, reconStats: data.reconStats,
    navigate, fetchData, handlePeriodChange, handleDeleteAllData, handleDeleteGstr1, handleDeleteGstr3b,
    handleUpload: upload.handleUpload, handleExport: upload.handleExport,
    handleToggleExclude: actions.handleToggleExclude, handleConfirmExclude: actions.handleConfirmExclude,
    handleBulkExclude: actions.handleBulkExclude,
    handleCategoryUpdate: actions.handleCategoryUpdate, onEditTxn: actions.onEditTxn,
  };
}
